import { useContentfulContentTypeEntry } from '@order/Contentful';

import {
  isOrganizationLinkFooterActionSupported,
  validContentfulData as fallback,
} from '../../utils';

export function useOrganizationLinksFooterContentfulData() {
  const {
    data: possibleContentfulData,
    fetching: isLoadingContentfulData,
    error: contentfulError,
  } = useContentfulContentTypeEntry<ContentfulOrganizationLinksFooter>({
    contentType: ORGANIZATION_LINKS_FOOTER_CONTENT_ID,
    include: 2,
  });

  const hasContentfulData = possibleContentfulData?.fields && !contentfulError;
  const contentfulData = hasContentfulData ? possibleContentfulData : fallback;

  const contentfulFooter = convertFooter(contentfulData.fields);
  const hasContentfulFooter = contentfulFooter?.sections?.length > 0;

  return {
    contentfulFooter,
    hasContentfulFooter,
    isLoadingContentfulData,
    contentfulError,
  };
}

// ─── Converters ────────────────────────────────────────────────────────

function convertFooter(
  footer?: ContentfulOrganizationLinksFooter,
): ContentfulOrganizationLinksFooterData {
  const sections = footer?.sections ?? [];

  return { sections: sections.filter(hasSectionFields).map(convertSection) };
}

function convertSection(section: ContentfulOrganizationLinksFooterSection) {
  return {
    name: section.fields.name,
    links: section.fields.links.filter(isLinkValid).map(convertLink),
  };
}

function convertLink(link: ContentfulOrganizationLinksFooterLink) {
  return link.fields;
}

function hasSectionFields(section: ContentfulOrganizationLinksFooterSection) {
  return Boolean(section.fields);
}

function isLinkValid(link: ContentfulOrganizationLinksFooterLink) {
  const isActionUnsupported = Boolean(
    link?.fields?.specialHandler &&
      !isOrganizationLinkFooterActionSupported(link.fields.specialHandler),
  );

  return Boolean(link.fields) && !isActionUnsupported;
}

// ─── Types ─────────────────────────────────────────────────────────────

type ContentfulOrganizationLinksFooter = {
  readonly name: string;
  readonly sections: readonly ContentfulOrganizationLinksFooterSection[];
};

type ContentfulOrganizationLinksFooterSection = {
  readonly fields: {
    readonly name: string;
    readonly links: readonly ContentfulOrganizationLinksFooterLink[];
  };
};

type ContentfulOrganizationLinksFooterLink = {
  readonly fields: {
    readonly label: string;
    readonly url: string;
    readonly specialHandler?: 'kustomer';
  };
};

type ContentfulOrganizationLinksFooterData = {
  readonly sections: ReadonlyArray<{
    readonly name: string;
    readonly links: ReadonlyArray<{
      readonly label: string;
      readonly url: string;
      readonly specialHandler?: 'kustomer';
    }>;
  }>;
};

// ─── Constants ─────────────────────────────────────────────────────────

const ORGANIZATION_LINKS_FOOTER_CONTENT_ID = 'organizationLinksFooter';
